/* eslint-disable jsx-a11y/anchor-has-content */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { trans } from '@spotahome/soyuz/client';
import { IconGeneric, useFiltersContext } from '@spotahome/ui-library';
import { useMediaQuery } from '@spotahome/ui-library/src/tenant/components/utils/breakpoints/breakpoints';

import adUrlService from '../../utils/ad-url-service';

import './Paginator.scss';

const ELLIPSIS = '...';
const PREV = 'prev';
const NEXT = 'next';
const PAGINATOR_ICONS = {
  prev: 'angle-left',
  next: 'angle-right'
};

const Ellipsis = () => <span className="new-paginator__ellipsis">...</span>;
const PageItem = ({
  page,
  pageUrl,
  couldBeHidden,
  currentPage,
  onPageClick
}) => {
  const isCurrentPage = page === currentPage;

  const handleClick = e => {
    e.preventDefault();
    onPageClick(page);
  };

  return (
    <a
      key={page}
      href={pageUrl}
      className={classNames('new-paginator__page-item', {
        'new-paginator__page-item--current': isCurrentPage
      })}
      aria-current={isCurrentPage ? 'page' : undefined}
      onClick={handleClick}
      data-test={`paginator-page-${page}`}
      tabIndex={isCurrentPage ? -1 : 0}
      data-hidden={couldBeHidden}
    >
      {page}
    </a>
  );
};

const PaginationButton = ({
  onClick,
  disabled,
  url,
  dataTest,
  ariaLabel,
  paginationRole = 'next'
}) => {
  const handleClick = e => {
    e.preventDefault();
    onClick();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-test={dataTest}
      disabled={disabled}
      className={`new-paginator__nav-button new-paginator__nav-button--${paginationRole}`}
      aria-label={ariaLabel}
    >
      {disabled ? (
        <span>
          <span className="new-paginator__nav-button-text">
            {trans(`paginator.label.${paginationRole}`)}
          </span>{' '}
          <IconGeneric name={PAGINATOR_ICONS[paginationRole]} />
        </span>
      ) : (
        <a href={url} tabIndex={-1} aria-hidden="true">
          <span className="new-paginator__nav-button-text">
            {trans(`paginator.label.${paginationRole}`)}
          </span>{' '}
          <IconGeneric name={PAGINATOR_ICONS[paginationRole]} />
        </a>
      )}
    </button>
  );
};

/**
 * Generates pagination items with intelligent ellipsis placement
 * Always ensures currentPage, first page (1), and last page are visible
 *
 * Examples:
 * - currentPage=1, totalPages=1: [1]
 * - currentPage=1, totalPages=10, maxPages=3: [1, ..., 10]
 * - currentPage=3, totalPages=10, maxPages=3: [1, ..., 3, ..., 10] (exceeds maxPages for essential info)
 * - currentPage=1, totalPages=10, maxPages=5: [1, 2, 3, ..., 10]
 * - currentPage=5, totalPages=10, maxPages=7: [1, ..., 4, 5, 6, ..., 10]
 */
const getPageNumbers = ({ currentPage, totalPages, maxPages }) => {
  const pages = [];

  // Edge cases
  if (totalPages <= 1) {
    return [1];
  }

  if (maxPages < 1) {
    return [];
  }

  // If we can show all pages, do it
  if (totalPages <= maxPages) {
    for (let i = 1; i <= totalPages; i += 1) {
      pages.push(i);
    }

    return pages;
  }

  // If current page is adjacent to first or last, we have simpler cases
  if (currentPage === 1 || currentPage === 2) {
    // Start case: show consecutive pages from beginning
    const maxConsecutive = Math.max(3, maxPages - 2); // At least show [1, 2, 3] or more
    const consecutivePages = Math.min(maxConsecutive, totalPages - 1);

    for (let i = 1; i <= consecutivePages; i += 1) {
      pages.push(i);
    }

    if (consecutivePages < totalPages - 1) {
      pages.push(ELLIPSIS);
    }

    if (consecutivePages < totalPages) {
      pages.push(totalPages);
    }
  } else if (currentPage === totalPages || currentPage === totalPages - 1) {
    // End case: show pages clustered at end
    pages.push(1);

    const maxConsecutive = Math.max(3, maxPages - 2); // At least show last 3 pages
    const startPage = Math.max(2, totalPages - maxConsecutive + 1);

    if (startPage > 2) {
      pages.push(ELLIPSIS);
    }

    for (let i = startPage; i <= totalPages; i += 1) {
      pages.push(i);
    }
  } else {
    // Middle case: ensure we always show [1, ..., currentPage, ..., totalPages]

    // Calculate actual minimal slots needed for [1, ..., currentPage, ..., totalPages]
    let minimalSlots = 3; // 1 + currentPage + totalPages
    if (currentPage > 2) minimalSlots += 1; // left ellipsis
    if (currentPage < totalPages - 1) minimalSlots += 1; // right ellipsis

    if (maxPages > minimalSlots) {
      // We have room to expand around currentPage
      const availableSlots = maxPages - 3; // Reserve slots for 1, currentPage, totalPages

      // Start with just currentPage
      let startRange = currentPage;
      let endRange = currentPage;
      let usedSlots = 1; // currentPage

      // Expand symmetrically while ensuring currentPage is always included
      while (usedSlots < availableSlots) {
        const canExpandLeft = startRange > 2;
        const canExpandRight = endRange < totalPages - 1;

        if (!canExpandLeft && !canExpandRight) break;

        // Prioritize expanding in the direction that keeps currentPage centered
        const leftDistance = currentPage - startRange;
        const rightDistance = endRange - currentPage;

        if (canExpandLeft && canExpandRight && usedSlots < availableSlots - 1) {
          // Can expand both directions
          if (leftDistance <= rightDistance) {
            startRange -= 1;
            usedSlots += 1;
          }
          if (usedSlots < availableSlots) {
            endRange += 1;
            usedSlots += 1;
          }
        } else if (canExpandLeft && usedSlots < availableSlots) {
          startRange -= 1;
          usedSlots += 1;
        } else if (canExpandRight && usedSlots < availableSlots) {
          endRange += 1;
          usedSlots += 1;
        } else {
          break;
        }
      }

      // Check if this would create too many slots with ellipses
      let totalSlots = 2; // first + last page
      if (startRange > 2) totalSlots += 1; // left ellipsis
      if (endRange < totalPages - 1) totalSlots += 1; // right ellipsis
      totalSlots += endRange - startRange + 1; // consecutive pages (excluding duplicates)

      // Shrink if needed
      while (totalSlots > maxPages && endRange - startRange + 1 > 1) {
        if (currentPage - startRange > endRange - currentPage) {
          startRange += 1;
        } else {
          endRange -= 1;
        }
        totalSlots -= 1;
      }

      // Build the array
      pages.push(1);

      if (startRange > 2) {
        pages.push(ELLIPSIS);
      }

      for (let i = startRange; i <= endRange; i += 1) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (endRange < totalPages - 1) {
        pages.push(ELLIPSIS);
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    } else {
      // Minimal case: just show [1, ..., currentPage, ..., totalPages]
      pages.push(1);

      if (currentPage > 2) {
        pages.push(ELLIPSIS);
      }

      if (currentPage !== 1 && currentPage !== totalPages) {
        pages.push(currentPage);
      }

      if (currentPage < totalPages - 1) {
        pages.push(ELLIPSIS);
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
  }

  return pages;
};

const useMaxPagesToShow = () => {
  const { isBiggerThanPhablet, isBiggerThanMobile } = useMediaQuery();

  if (isBiggerThanPhablet()) {
    return 7;
  } else if (isBiggerThanMobile()) {
    return 6;
  }

  return 5;
};

const Paginator = ({
  currentPage = 1,
  totalPages = 1,
  prevPage,
  nextPage,
  previousButtonDisabled = false,
  nextButtonDisabled = false,
  onPreviousButtonClick = () => {},
  onNextButtonClick = () => {},
  onPageClick = () => {},
  locationString
}) => {
  const { filters } = useFiltersContext();

  const getPageUrl = page => {
    return adUrlService.getSearchUrlFromFilters(locationString, {
      ...filters,
      page
    });
  };

  const maxPagesToShow = useMaxPagesToShow();

  const pageItems = useMemo(
    () => getPageNumbers({ currentPage, totalPages, maxPages: maxPagesToShow }),
    [currentPage, totalPages, maxPagesToShow]
  );

  return (
    <div className="new-paginator">
      {/* Previous button */}
      <PaginationButton
        onClick={onPreviousButtonClick}
        disabled={previousButtonDisabled}
        url={previousButtonDisabled ? '' : getPageUrl(prevPage)}
        dataTest="paginator-btn-prev"
        ariaLabel={trans('paginator.label.previous')}
        paginationRole={PREV}
      />
      {/* Page numbers */}
      <div className="new-paginator__pages">
        {pageItems.map((page, index) =>
          page === ELLIPSIS ? (
            <Ellipsis key={`ellipsis-${index}`} />
          ) : (
            <PageItem
              key={page}
              page={page}
              onPageClick={onPageClick}
              currentPage={currentPage}
              pageUrl={getPageUrl(page)}
              couldBeHidden={
                (page < currentPage - 1 || page > currentPage + 1) &&
                page !== 1 &&
                page !== totalPages &&
                page > maxPagesToShow &&
                page < totalPages - maxPagesToShow
              }
            />
          )
        )}
      </div>
      {/* Next button */}
      <PaginationButton
        onClick={onNextButtonClick}
        disabled={nextButtonDisabled}
        url={nextButtonDisabled ? '' : getPageUrl(nextPage)}
        dataTest="paginator-btn-next"
        ariaLabel={trans('paginator.label.next')}
        paginationRole={NEXT}
      />
    </div>
  );
};

Paginator.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  previousButtonDisabled: PropTypes.bool,
  nextButtonDisabled: PropTypes.bool,
  onPreviousButtonClick: PropTypes.func,
  onNextButtonClick: PropTypes.func,
  onPageClick: PropTypes.func,
  prevPage: PropTypes.number.isRequired,
  nextPage: PropTypes.number.isRequired,
  locationString: PropTypes.string.isRequired
};

export default Paginator;
